import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ArrowLeft, BarChart3, Phone, CheckCircle2, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useStatusFilter } from "@/context/StatusFilterContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const METRICS = [
  { key: "health", label: "Sağlık Skoru", color: "#0F2C59", axis: "right" },
  { key: "kesin", label: "Kesin", color: "#16A34A", axis: "left" },
  { key: "belki", label: "Belki", color: "#F59E0B", axis: "left" },
  { key: "gelmeyecek", label: "Gelmeyecek", color: "#64748B", axis: "left" },
  { key: "ulasilamadi", label: "Ulaşılamadı", color: "#0EA5E9", axis: "left" },
  { key: "karsi", label: "Karşı", color: "#DC2626", axis: "left" },
  { key: "belge", label: "Yetki Belgesi", color: "#7C3AED", axis: "left" },
];
const SHARE_PARTS = [
  { key: "kesin", label: "Kesin (bizde)", color: "#16A34A" },
  { key: "belki", label: "Belki", color: "#F59E0B" },
  { key: "karsi", label: "Karşı", color: "#DC2626" },
  { key: "ulasilamadi", label: "Ulaşılamadı", color: "#0EA5E9" },
  { key: "gelmeyecek", label: "Gelmeyecek", color: "#64748B" },
  { key: "bos", label: "İşaretsiz", color: "#CBD5E1" },
];
const RANGES = [
  ["1", "Günlük"], ["3", "3 Gün"], ["5", "5 Gün"], ["7", "Haftalık"], ["30", "Aylık"], ["all", "Tüm Zamanlar"],
];
const axis = { fontSize: 11, fill: "#94a3b8" };
const grid = "#1e293b";
const heatColor = (h) => (h >= 80 ? "#16A34A" : h >= 60 ? "#65A30D" : h >= 40 ? "#F59E0B" : "#DC2626");

const RefTick = ({ cx, cy }) => {
  if (cx == null || cy == null) return null;
  return <line x1={cx - 13} x2={cx + 13} y1={cy} y2={cy} stroke="#000000" strokeWidth={3.5}
    strokeDasharray="5 3" style={{ pointerEvents: "none" }} />;
};

function Tile({ m, active, total, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`metric-tile-${m.key}`}
      className="relative rounded-xl px-4 py-3 text-left transition-all duration-150 select-none"
      style={{
        background: active ? m.color : "var(--card, #0b1220)",
        color: active ? "#fff" : "inherit",
        border: `1px solid ${active ? m.color : "rgba(148,163,184,0.35)"}`,
        boxShadow: active
          ? `0 8px 0 ${m.color}66, 0 12px 22px ${m.color}55`
          : "0 6px 0 rgba(15,23,42,0.35), 0 8px 14px rgba(0,0,0,0.25)",
        transform: active ? "translateY(2px)" : "translateY(-2px)",
      }}
    >
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: active ? "#fff" : m.color }} />
        <span className="text-sm font-semibold">{m.label}</span>
      </div>
      <div className="text-lg font-bold mt-1 tabular-nums" style={{ color: active ? "#fff" : m.color }}>
        {m.key === "health" ? `Ø ${total}` : total}
      </div>
    </button>
  );
}

export default function CommitteeAnalytics() {
  const memT = useTableSort([]);
  const { status } = useStatusFilter();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState({ health: true, kesin: true, karsi: true, belki: false, gelmeyecek: false, ulasilamadi: false });
  const [selected, setSelected] = useState([]); // committee_no list; empty = all
  const [sort, setSort] = useState("no");
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState("7");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(`/dashboard/committee-analytics?status=${status}`).then(({ data }) => setRows(data.committees || [])).catch(() => setRows([]));
  }, [status]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => setUsers([])); }, []);

  const shown = useMemo(() => {
    if (!rows) return [];
    let list = selected.length ? rows.filter((r) => selected.includes(r.committee_no)) : rows;
    if (sort === "health") list = [...list].sort((a, b) => a.health - b.health);
    else if (sort === "kesin") {
      const pct = (r) => (r.faal ? (r.kesin || 0) / r.faal : 0);
      list = [...list].sort((a, b) => pct(a) - pct(b));
    }
    else if (sort === "karsi") {
      const pct = (r) => (r.faal ? (r.karsi || 0) / r.faal : 0);
      list = [...list].sort((a, b) => pct(b) - pct(a));
    }
    return list;
  }, [rows, selected, sort]);

  const weakest = useMemo(() => (rows ? [...rows].sort((a, b) => a.health - b.health).slice(0, 5) : []), [rows]);

  const shareData = useMemo(() => shown.map((r) => {
    const base = SHARE_PARTS.reduce((a, p) => a + (r[p.key] || 0), 0) || 1;
    const o = { committee_no: r.committee_no, name: r.name, _base: base,
      ref_pct: r.ref_pct ?? null, part_2018_pct: r.part_2018_pct ?? null };
    SHARE_PARTS.forEach((p) => {
      o[p.key] = Math.round(((r[p.key] || 0) / base) * 1000) / 10;
      o[`${p.key}_n`] = r[p.key] || 0;
    });
    return o;
  }), [shown]);

  const totals = useMemo(() => {
    const t = {};
    METRICS.forEach((m) => {
      if (m.key === "health") {
        t[m.key] = shown.length ? Math.round(shown.reduce((a, r) => a + (r.health || 0), 0) / shown.length) : 0;
      } else {
        t[m.key] = shown.reduce((a, r) => a + (r[m.key] || 0), 0);
      }
    });
    return t;
  }, [shown]);

  // Tek komite seçiliyse detay getir
  const soleCno = selected.length === 1 ? selected[0] : null;
  useEffect(() => {
    if (soleCno == null) { setDetail(null); return; }
    setDetail(null);
    api.get(`/analytics/committee/${soleCno}?status=${status}`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [soleCno, status]);

  // Zaman çizelgesi
  useEffect(() => {
    setTimeline(null);
    const params = new URLSearchParams({ status });
    if (soleCno != null) params.set("committee_no", String(soleCno));
    if (custom.from || custom.to) {
      if (custom.from) params.set("date_from", custom.from);
      if (custom.to) params.set("date_to", custom.to);
    } else if (range === "all") {
      params.set("days", "0");
    } else {
      params.set("days", range);
    }
    api.get(`/analytics/committee-timeline?${params.toString()}`)
      .then(({ data }) => setTimeline(data))
      .catch(() => setTimeline({ days: [] }));
  }, [status, soleCno, range, custom.from, custom.to]);

  const toggleMetric = (k) => setActive((a) => ({ ...a, [k]: !a[k] }));  const toggleCommittee = (cno) => setSelected((s) => (s.includes(cno) ? s.filter((x) => x !== cno) : [...s, cno]));
  const activeMetrics = METRICS.filter((m) => active[m.key]);

  const managersOf = (cno) => users.filter((u) => (u.role === "committee_manager" || u.role === "super_admin") && (u.all_committees || (u.committee_nos || []).includes(cno) || u.committee_no === cno));

  if (rows === null) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}</div>;

  return (
    <div className="space-y-4" data-testid="committee-analytics-page">
      <div>
        <button onClick={() => navigate("/karne")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="ca-back"><ArrowLeft className="h-3.5 w-3.5" /> Komite Karnesi</button>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-6 w-6 text-cyan-500" /> Komite Analizleri</h1>
        <p className="text-sm text-muted-foreground">48 komiteyi tek grafikte karşılaştırın; kutucukları seçerek çizgileri açın, komiteleri seçerek detaya inin. ({status === "faal" ? "Faal" : status === "aski" ? "Askı" : "Tümü"})</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="metric-tiles">
        {METRICS.map((m) => <Tile key={m.key} m={m} active={active[m.key]} total={totals[m.key]} onClick={() => toggleMetric(m.key)} />)}
      </div>

      <Card className="p-4 border border-border">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-display font-semibold text-sm">Komite Bazlı Çoklu Metrik — Oy Payı (%)</h3>
            <p className="text-[11px] text-muted-foreground">Her komitede tarafların yüzde payı · taban: {status === "faal" ? "faal" : status === "aski" ? "askıda" : "tüm"} firma sayısı · kesikli iz çizgisi: 2018 katılımı ÷ 0,85</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="ca-sort">
              {[["no", "Komite No"], ["health", "Sağlık ↑"], ["kesin", "Kesin Oy Oranı ↑"], ["karsi", "Karşı Oy Oranı ↓"]].map(([k, l]) => (
                <button key={k} onClick={() => setSort(k)} data-testid={`ca-sort-${k}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${sort === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{selected.length ? `${selected.length} komite seçili` : "Tüm komiteler (48)"}</span>
          </div>
        </div>
        <div className="h-[520px] w-full" data-testid="ca-share-chart">
          <ResponsiveContainer>
            <BarChart data={shareData} margin={{ top: 8, right: 12, bottom: 8, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="committee_no" tick={axis} interval={0} angle={-45} textAnchor="end" height={44} />
              <YAxis tick={axis} width={46} tickFormatter={(v) => `${v}%`} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
              <Tooltip
                contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(v) => { const r = shareData.find((x) => x.committee_no === v); return r ? `#${v} ${r.name} · ${r._base} firma` : `Komite ${v}`; }}
                formatter={(val, name, item) => {
                  if (name === "2018 katılımı ÷ 0,85") {
                    const p = item?.payload?.part_2018_pct;
                    return [`%${(val || 0).toFixed(1)}${p != null ? ` (2018 katılımı %${p})` : ""}`, name];
                  }
                  const p = SHARE_PARTS.find((x) => x.label === name);
                  const n = p ? item?.payload?.[`${p.key}_n`] : null;
                  return [`%${(val || 0).toFixed(1)}${n != null ? ` · ${n} firma` : ""}`, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {(sort === "karsi"
                ? SHARE_PARTS.map((p) => (p.key === "kesin" ? SHARE_PARTS[2] : p.key === "karsi" ? SHARE_PARTS[0] : p))
                : SHARE_PARTS
              ).map((p) => (
                <Bar key={p.key} dataKey={p.key} name={p.label} stackId="share" fill={p.color} />
              ))}
              <Scatter dataKey="ref_pct" name="2018 katılımı ÷ 0,85" legendType="line"
                fill="#000000" shape={RefTick} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 border border-border">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display font-semibold text-sm">Metrik Karşılaştırma (adet)</h3>
          <span className="text-xs text-muted-foreground">{activeMetrics.length} metrik</span>
        </div>
        <div className="h-[520px] w-full">
          {activeMetrics.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-muted-foreground">Görüntülemek için yukarıdan en az bir kutucuk seçin.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={shown} margin={{ top: 8, right: 12, bottom: 8, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="committee_no" tick={axis} interval={0} angle={-45} textAnchor="end" height={44} />
                <YAxis yAxisId="left" tick={axis} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={axis} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(v) => { const r = shown.find((x) => x.committee_no === v); return r ? `#${v} ${r.name}` : `Komite ${v}`; }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {activeMetrics.map((m) => (
                  <Line key={m.key} yAxisId={m.axis} type="monotone" dataKey={m.key} name={m.label} stroke={m.color} strokeWidth={m.key === "health" ? 2.5 : 2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card className="p-4 border border-border" data-testid="ca-timeline-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-display font-semibold text-sm">Zaman İçinde Performans</h3>
            <p className="text-[11px] text-muted-foreground">
              {soleCno != null ? `#${soleCno} komitenin` : "Tüm komitelerin"} gün gün değişimi
              {timeline?.days?.length ? ` · ${timeline.days[0].label} → ${timeline.days[timeline.days.length - 1].label}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="ca-range">
              {RANGES.map(([k, l]) => (
                <button key={k} onClick={() => { setRange(k); setCustom({ from: "", to: "" }); }} data-testid={`ca-range-${k}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${range === k && !custom.from && !custom.to ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="ca-range-from" />
              <span className="text-xs text-muted-foreground">–</span>
              <input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="ca-range-to" />
            </div>
          </div>
        </div>
        <div className="h-[360px] w-full">
          {!timeline ? (
            <Skeleton className="h-full w-full rounded-lg" />
          ) : activeMetrics.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-muted-foreground">Görüntülemek için yukarıdan en az bir kutucuk seçin.</div>
          ) : timeline.days.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-muted-foreground" data-testid="ca-timeline-empty">Bu aralıkta veri yok.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={timeline.days} margin={{ top: 8, right: 12, bottom: 8, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="label" tick={axis} />
                <YAxis yAxisId="left" tick={axis} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={axis} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {activeMetrics.map((m) => (
                  <Line key={m.key} yAxisId={m.axis} type="monotone" dataKey={m.key} name={m.label} stroke={m.color} strokeWidth={m.key === "health" ? 2.5 : 2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card className="p-4 border border-border">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-display font-semibold text-sm">Komite Seçimi</h3>
          <button type="button" onClick={() => setSelected([])} data-testid="ca-select-all"
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${selected.length === 0 ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground hover:text-foreground"}`}>
            Tümü (48)
          </button>
        </div>
        <div className="mb-2 flex items-center gap-1.5 flex-wrap text-xs" data-testid="ca-weakest">
          <span className="text-red-600 font-semibold">En zayıf 5 komite:</span>
          {weakest.map((r) => (
            <button key={r.committee_no} onClick={() => setSelected([r.committee_no])} data-testid={`ca-weak-${r.committee_no}`}
              className="px-2 py-1 rounded-md bg-red-500/10 text-red-600 border border-red-500/30 hover:bg-red-500/20 font-medium">
              #{r.committee_no} · %{r.health}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="committee-chips">
          {rows.map((r) => {
            const sel = selected.includes(r.committee_no);
            return (
              <button key={r.committee_no} type="button" onClick={() => toggleCommittee(r.committee_no)} data-testid={`ca-chip-${r.committee_no}`}
                title={`${r.name} · Sağlık %${r.health} · Kesin ${r.kesin}`}
                className={`h-8 min-w-[2rem] px-2 text-xs font-bold rounded-md border transition-all ${sel ? "ring-2 ring-cyan-300 border-cyan-600" : "border-transparent hover:opacity-80"}`}
                style={sel ? { background: "#0891b2", color: "#fff" } : { background: heatColor(r.health), color: "#fff" }}>
                {r.committee_no}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Isı rengi = sağlık skoru: <span className="text-red-500 font-semibold">kırmızı zayıf</span> → <span className="text-amber-500 font-semibold">sarı orta</span> → <span className="text-green-600 font-semibold">yeşil güçlü</span></p>
      </Card>

      {soleCno != null && (
        <Card className="p-4 border border-cyan-500/40" data-testid="committee-detail-panel">
          {!detail ? <Skeleton className="h-40 rounded-lg" /> : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-display font-semibold text-base">#{detail.committee_no} {detail.name}</h3>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded bg-muted">Kayıtlı: <b>{detail.summary.registered}</b></span>
                  <span className="px-2 py-1 rounded bg-muted">Ulaşılan: <b>{detail.summary.reached}</b></span>
                  <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-600">Kesin: <b>{detail.summary.bize}</b></span>
                  <span className="px-2 py-1 rounded bg-red-500/10 text-red-600">Karşı: <b>{detail.summary.karsi}</b></span>
                  <span className="px-2 py-1 rounded bg-amber-500/10 text-amber-600">Çalışma: <b>%{detail.summary.work_score}</b></span>
                  <span className="px-2 py-1 rounded bg-violet-500/10 text-violet-600" data-testid="ca-detail-belge">Yetki Belgesi: <b>{detail.summary.belge}/{detail.summary.belge_target}</b> (%{detail.summary.belge_pct})</span>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1"><Users2 className="h-3.5 w-3.5" /> Komite Yöneticileri</p>
                <div className="flex flex-wrap gap-1.5">
                  {managersOf(detail.committee_no).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                  {managersOf(detail.committee_no).map((u) => (
                    <span key={u.id} className="text-xs px-2 py-1 rounded-md bg-[#0F2C59] text-white">{u.name}{u.all_committees ? " · Tüm komiteler" : ""}</span>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-1">Sorumlular — aramaları (ulaşılan) ve aldıkları oylar (kesin)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b border-border">
                    <memT.Th sortKey="ca-sorumlu" get={(r) => r.name} text className="text-left py-2">Sorumlu</memT.Th>
                    <memT.Th sortKey="ca-atanan" get={(r) => r.assigned} className="text-center py-2">Atanan Firma</memT.Th>
                    <memT.Th sortKey="ca-arama" get={(r) => r.contacted} className="text-center py-2"><span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> Arama (Ulaşılan)</span></memT.Th>
                    <memT.Th sortKey="ca-kesin" get={(r) => r.kesin} className="text-center py-2"><span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Aldığı Oy (Kesin)</span></memT.Th>
                  </tr></thead>
                  <tbody>
                    {memT.applySort(detail.members || []).map((m, i) => (
                      <tr key={i} className="border-b border-border/40" data-testid={`ca-member-${i}`}>
                        <td className="py-2">{m.name}</td>
                        <td className="text-center py-2 tabular-nums">{m.assigned}</td>
                        <td className="text-center py-2 tabular-nums text-cyan-600 font-semibold">{m.reached}</td>
                        <td className="text-center py-2 tabular-nums text-emerald-600 font-semibold">{m.kesin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
